import { Module, VuexModule, MutationAction, getModule, Mutation, Action } from 'vuex-module-decorators';
import {
    Customer,
    LoginRequest,
    Address,
    RegistrationRequest,
    Address as AddressModel,
    UserRegistrationRequest,
} from '@/store/models';
import {
    login,
    setJWT,
    getUserInfo,
    clearJWT,
    forgotPassword,
    userRegistration,
    userSaveData,
    deleteCustomerAddress,
} from '../api';
import store from '@/store';
import cart from '@/store/modules/cart';
import checkout from '@/store/modules/checkout';
import { initState } from '@/store/stateinit';
import to from 'await-to-js';
import handleError from '@/commons/errorHandler';
import addressNormalizer, { compareAddress, copyAddressData, customerAddressPrototype, quoteAddressToCustomerAddress } from '@/commons/addressNormalizer';
import { add, cloneDeep } from 'lodash';
import { i18n } from '@/i18n';

@Module({
    namespaced: true,
    name: 'user',
    store,
    dynamic: true,
})
class UsersModule extends VuexModule {
    public customer: Customer = initState('customer');
    public token: string = initState('token');
    public editAddressMode: string = '';
    public editAddress = {};
    public currentSelectedAddressId = 0;

    get isLoggedin(): boolean {
        return this.token.toString() !== '';
    }

    get isFidelity(): boolean {
        return this.isLoggedin && this.customer.extension_attributes && this.customer.extension_attributes.fidelity !== '';
    }

    get shippingAddress(): Address|undefined {
        return this.customer.addresses.find((a: Address) => a.default_shipping);
    }

    get billingAddress(): Address|undefined {
        return this.customer.addresses.find((a: Address) => a.default_billing);
    }

    get hasAddresses(): boolean {
        return this.customer.addresses.length > 0;
    }

    @Mutation
    public setCustomer(customer: Customer) {
        this.customer = customer;
    }

    @Mutation
    public setEmail(email: string) {
        this.customer.email = email;
    }

    @Mutation
    public setToken(token: string) {
        this.token = token;
    }

    @Mutation
    public setEditMode(payload: {mode: string, selectedId: number|undefined}) {
        this.editAddressMode = payload.mode;
        if (payload.mode === 'new') {
            this.editAddress = customerAddressPrototype();
        } else if (payload.mode === 'update') {
            const custAddr = this.customer.addresses.find((a: any) => a.id === payload.selectedId) as unknown as any;
            this.editAddress = addressNormalizer(custAddr);
        }
    }

    @Mutation
    public removeAddress(addressId: number) {
        const idx = this.customer.addresses.findIndex((a) => a.id == addressId);
        if (idx !== -1) {
            this.customer.addresses.splice(idx, 1);
        }
    }

    @Mutation
    public setCurrentSelectedAddress(addressId: number) {
        this.currentSelectedAddressId = addressId;
    }

    @Action
    public async login(loginRequest: LoginRequest): Promise<string> {
        let token: any;
        let err: any;
        [ err, token ] = await to(login(loginRequest));
        return new Promise((resolve, reject) => {
            if (err) {
                clearJWT();
                token = '';
                handleError(err, false);
                reject(err);
            }
            if (token) {
                setJWT(token);
                // facciamo il redirect
                // this.getMe().then((res) => {
                //     bus.$emit(loginEvent, this.customer);
                // });
                resolve(token);

                cart.setToken('');
            }
            this.setToken(token);
        });
    }

    @Action
    public async sendForgotPassword(email: string): Promise<boolean> {
        return forgotPassword(email);
    }

    @Action({rawError: true})
    public async saveUserAddress({ address, mode, addressType, editingAddressId }: any): Promise<any> {
        const oldAddressIds = this.customer.addresses.map((a) => a.id);
        const normalized = quoteAddressToCustomerAddress(address) as AddressModel;
        const userObj = cloneDeep(this.customer) as unknown as UserRegistrationRequest;
        normalized.default_shipping = true;
        normalized.default_billing = true;

        return new Promise((resolve, reject) => {
            if (mode === 'new') {
                if (this.customer.addresses.length && compareAddress(normalized, this.customer.addresses)) {
                    reject(i18n.t('The address is the same as another in your address book').toString());
                }
                delete normalized.id;
                for (const a of userObj.addresses) {
                    a.default_shipping = false;
                    a.default_billing = false;
                }
                userObj.addresses.push(normalized);
            } else if (mode === 'update') {
                const userAddr = this.customer.addresses.find((a: any) => a.id === editingAddressId)
                    || this.customer.addresses[0];
                copyAddressData(normalized, userAddr);
            }

            userSaveData(userObj)
                .then((res) => {
                    this.setEditMode({mode: '', selectedId: undefined});

                    let newAddress;
                    for (const a of res.addresses) {
                        if (!oldAddressIds.includes(a.id)) {
                            newAddress = a;
                            break;
                        }
                    }

                    if (mode === 'new' && newAddress) {
                        this.customer.addresses.push(newAddress);
                        checkout.setSelectedAddressId({ type: addressType, id: newAddress.id });
                    }
                    if (addressType === 'shipping') {
                        copyAddressData(address, checkout.shippingAddress);
                    }
                    if (addressType === 'billing') {
                        copyAddressData(address, checkout.billingAddress);
                        copyAddressData(address, checkout.shippingAddress);
                    }

                    resolve(newAddress);
                })
                .catch((err) => {
                    reject(handleError(err, false, false));
                });
        });
    }

    @Action({rawError: true})
    public async register(data: RegistrationRequest): Promise<boolean> {
        return userRegistration(data);
    }

    @Action({rawError: true})
    public async deleteAddress(addressId: number): Promise<boolean> {
        deleteCustomerAddress(addressId);
        return new Promise((resolve, reject) => {
            this.removeAddress(addressId);
            resolve(true);
        });
    }

    @Action
    public copySelectedAddressToQuoteAddress(addressType: string){
        const address = this.customer.addresses.find((a: Address) => a.id === this.currentSelectedAddressId);
        if (address) {
            const quoteAddress = addressType === 'shipping' ? checkout.shippingAddress : checkout.billingAddress;
            copyAddressData(address, quoteAddress);
            this.setCurrentSelectedAddress(0);
        }
        return new Promise((resolve, reject) => {
            resolve(true);
        });
    }

    @MutationAction({ mutate: ['customer'] })
    public async getMe() {
        let customer;
        let err;
        [ err, customer ] = await to(getUserInfo());
        return { customer };
    }
}

export default getModule(UsersModule);

